import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InsuranceApiService, InsuranceUwCaseListItem } from './insurance-api.service';

@Component({
  standalone: true,
  selector: 'ain-insurance-uw-inbox',
  imports: [
    AinDatePipe,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule
  ],
  template: `
    <ain-page [title]="t('insurance.uwInbox.title')" [description]="t('insurance.uwInbox.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.uw.help.page.title'"
        [descriptionKey]="'insurance.uw.help.page.description'"
        [bulletKeys]="[
          'insurance.uw.help.page.bullets.scope',
          'insurance.uw.help.page.bullets.filters',
          'insurance.uw.help.page.bullets.selection'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('insurance.states.noPermission')" [message]="t('insurance.states.noPermissionMessage')" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.open')" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('insurance.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'insurance.uw.help.listFilters.title'"
                [descriptionKey]="'insurance.uw.help.listFilters.description'"
                [bulletKeys]="[
                  'insurance.uw.help.listFilters.bullets.search',
                  'insurance.uw.help.listFilters.bullets.status'
                ]" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('insurance.uwInbox.filters.search') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
              <label>
                <span>{{ t('insurance.uwInbox.filters.status') }}</span>
                <p-select
                  [options]="statusOptions"
                  optionValue="value"
                  [(ngModel)]="selectedStatus"
                  (ngModelChange)="onStatusChanged($event)"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
            </div>
          </details>

          @if (filteredRows().length === 0) {
            <ain-state type="empty" [title]="t('insurance.uwInbox.states.empty')" [message]="t('insurance.uwInbox.states.emptyMessage')" />
          } @else {
            <p-table
              [value]="filteredRows()"
              dataKey="id"
              [paginator]="true"
              [rows]="10"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th>{{ t('insurance.uwInbox.fields.proposalNumber') }}</th>
                  <th>{{ t('columns.status') }}</th>
                  <th>{{ t('insurance.uwInbox.fields.riskScore') }}</th>
                  <th>{{ t('insurance.uwInbox.fields.workflowInstanceId') }}</th>
                  <th>{{ t('insurance.uwInbox.fields.createdAt') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.proposalNumber }}</button></td>
                  <td>{{ t(statusKey(row.status)) }}</td>
                  <td>{{ row.riskScore ?? '-' }}</td>
                  <td>{{ workflowInstanceLabel(row.workflowInstanceId) }}</td>
                  <td>{{ row.createdAtUtc | ainDate }}</td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; }
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .filters { border: 1px solid #e5e7eb; border-radius: 10px; padding: 8px 10px; background: #f9fafb; }
    .filters summary { display: flex; align-items: center; gap: 8px; }
    .filter-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-top: 8px; }
    .filter-grid label { display: grid; gap: 6px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) { .filter-grid { grid-template-columns: 1fr; } }
  `]
})
export class InsuranceUwInboxComponent implements OnInit {
  private readonly api = inject(InsuranceApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly error = signal('');
  readonly permissionDenied = signal(false);
  readonly rows = signal<InsuranceUwCaseListItem[]>([]);
  readonly selectedRows = signal<InsuranceUwCaseListItem[]>([]);
  readonly search = signal('');
  readonly statusFilter = signal<string | null>(null);
  selectedStatus: string | null = null;
  readonly statusOptions = [
    { value: 'Open', labelKey: 'insurance.uw.status.open' },
    { value: 'Referred', labelKey: 'insurance.uw.status.referred' },
    { value: 'PendingInfo', labelKey: 'insurance.uw.status.pendingInfo' },
    { value: 'Approved', labelKey: 'insurance.uw.status.approved' },
    { value: 'Declined', labelKey: 'insurance.uw.status.declined' }
  ];

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.api.listUwCases(this.statusFilter() ?? undefined).subscribe({
      next: rows => {
        this.rows.set(rows);
        this.error.set('');
        this.permissionDenied.set(false);
        this.loading.set(false);
      },
      error: err => {
        this.rows.set([]);
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('insurance.states.loadError'));
        this.loading.set(false);
      }
    });
  }

  filteredRows(): InsuranceUwCaseListItem[] {
    const value = this.search().trim().toLowerCase();
    if (!value) {
      return this.rows();
    }

    return this.rows().filter(row =>
      row.proposalNumber.toLowerCase().includes(value)
      || row.status.toLowerCase().includes(value)
      || (row.decisionReason ?? '').toLowerCase().includes(value));
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
  }

  onStatusChanged(value: string | null): void {
    this.statusFilter.set(value);
    this.selectedStatus = value;
    this.load();
  }

  onSelectionChange(selection: InsuranceUwCaseListItem[] | InsuranceUwCaseListItem | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (row) {
      this.openDetail(row.id);
    }
  }

  openDetail(id: string): void {
    void this.router.navigate(['/insurance/uw-cases', id]);
  }

  workflowInstanceLabel(workflowInstanceId?: string | null): string {
    return workflowInstanceId
      ? this.t('insurance.uwInbox.fields.workflowLinked')
      : this.t('insurance.uwInbox.fields.workflowNotLinked');
  }

  statusKey(status: string): string {
    return `insurance.uw.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
